const publicSeo = require("../config/publicSeoRoutes.json");
const { applyMemberSeoTitleExperiment } = require("./memberSeoTitleExperiment.shared");
const memberSeoCityInventory = require("../config/memberSeoCities.generated.json");
const memberSeoTaskRegistry = require("../config/memberSeoTasks.json");

const MEMBER_SEO_DEFAULT_TASK_SLUG = "senior-household-help";
const MEMBER_SEO_ROUTE_PATTERN = "/{task}/{state}/{city}";

const assertUniqueMemberSeoTasks = (tasks = []) => {
  const seen = {
    taskSlug: new Set(),
    taskKey: new Set(),
    canonicalBasePath: new Set(),
    productAlias: new Set(),
  };
  const claim = (kind, value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) {
      throw new Error(`Member SEO task is missing ${kind}.`);
    }
    if (seen[kind].has(normalized)) {
      throw new Error(`Duplicate member SEO ${kind}: ${normalized}`);
    }
    seen[kind].add(normalized);
  };
  tasks.forEach((task) => {
    claim("taskSlug", task.taskSlug);
    claim("taskKey", task.taskKey);
    claim("canonicalBasePath", task.canonicalBasePath);
    if (task.productTaskSelectable) {
      (task.productTaskAliases || []).forEach((alias) =>
        claim("productAlias", alias),
      );
    }
  });
  return true;
};

const normalizeMemberSeoTask = (task = {}) =>
  Object.assign({}, task, {
    signupSource: "member_city_seo",
    landingSource: "member_city_seo",
    seoVersion: memberSeoTaskRegistry.version,
    lastmod: task.lastmod || memberSeoTaskRegistry.lastmod,
    serviceSchema: {
      examples: (task.examples || []).map((example) => example.title),
    },
    localSeo: {
      titleTemplate: task.titleTemplate,
      descriptionTemplate: task.descriptionTemplate,
      h1Template: task.h1,
      supportingCopyTemplate: task.supportingCopyTemplate,
      bodyCopyTemplate: task.bodyCopyTemplate,
      taskCallout: {
        eyebrow: task.calloutEyebrow,
        titleTemplate: task.calloutTitle,
        bodyTemplate: task.calloutBodyTemplate,
        visitExamples: task.examples || [],
      },
    },
  });

const rawMemberSeoTasks = memberSeoTaskRegistry.tasks || [];
assertUniqueMemberSeoTasks(rawMemberSeoTasks);
const MEMBER_SEO_TASKS = Object.fromEntries(
  rawMemberSeoTasks.map((task) => [
    task.taskSlug,
    normalizeMemberSeoTask(task),
  ]),
);

const normalizeProductTaskAlias = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const memberSeoTaskByProductAlias = new Map(
  Object.values(MEMBER_SEO_TASKS).flatMap((task) =>
    task.productTaskSelectable
      ? (task.productTaskAliases || []).map((alias) => [
          normalizeProductTaskAlias(alias),
          task,
        ])
      : [],
  ),
);

const latestIsoDate = (...values) => {
  const dates = values.filter(Boolean).sort();
  return dates[dates.length - 1] || "";
};

const MEMBER_SEO_CANONICAL_DECISIONS = {
  routePattern: MEMBER_SEO_ROUTE_PATTERN,
  stateSlugFormat: "full-state-name-slug",
  citySlugFormat: "city-name-slug",
  initialTaskSlug: MEMBER_SEO_DEFAULT_TASK_SLUG,
  indexingLaunchState: "member-seo-route-family-enabled",
  registryVersion: memberSeoTaskRegistry.version,
  cityTitlePattern: "{Canonical Task Name} in {City}, {ST} | Linked Lives",
  h1WordRange: [3, 6],
};

const normalizePath = (routePath = "/") => {
  if (!routePath || routePath === "/") return "/";
  return `/${String(routePath).replace(/^\/+|\/+$/gu, "")}`;
};

const absoluteUrl = (routePath, siteUrl = publicSeo.siteUrl) => {
  const normalized = normalizePath(routePath);
  return `${siteUrl}${normalized === "/" ? "/" : normalized}`;
};

const getMemberSeoTask = (taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG) =>
  MEMBER_SEO_TASKS[taskSlug] || null;

const getMemberSeoTaskByProductAlias = (alias = "") =>
  memberSeoTaskByProductAlias.get(normalizeProductTaskAlias(alias)) || null;

const stateSlugFor = (city = {}) => String(city.slug || "").split("/")[0] || "";
const citySlugFor = (city = {}) => String(city.slug || "").split("/")[1] || "";

const getMemberSeoServiceRoute = (taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG) => {
  const task = getMemberSeoTask(taskSlug);
  if (!task) return {};
  return (
    publicSeo.routes.find((route) => route.serviceKey === task.serviceKey) || {}
  );
};

const getMemberSeoTaskSlugs = () => Object.keys(MEMBER_SEO_TASKS);

const getMemberSeoTaskRootPath = (taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG) => {
  const task = getMemberSeoTask(taskSlug);
  return normalizePath(task?.canonicalBasePath || "/");
};

const getMemberSeoCityPath = (
  city = {},
  taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG,
) => {
  const task = getMemberSeoTask(taskSlug);
  if (!task) return "/";
  const stateSlug = city.stateSlug || stateSlugFor(city);
  const citySlug = city.citySlug || citySlugFor(city);
  return normalizePath(`${task.canonicalBasePath}/${stateSlug}/${citySlug}`);
};

const buildMemberSeoLocationContext = (
  city = {},
  taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG,
) => {
  const task = getMemberSeoTask(taskSlug);
  const stateSlug = city.stateSlug || stateSlugFor(city);
  const citySlug = city.citySlug || citySlugFor(city);

  return {
    source: task?.landingSource || "member_city_seo",
    sourceFamily: "member_seo",
    taskSlug,
    taskLabel: task?.label || "",
    taskGroup: task?.taskKey || taskSlug,
    serviceKey: task?.serviceKey || "",
    seoVersion: task?.seoVersion || "",
    seoCohort: task?.cohort || "",
    stateSlug,
    citySlug,
    city: city.city || "",
    stateCode: city.stateCode || "",
    stateName: city.stateName || "",
    primaryPostalCode: city.primaryPostalCode || "",
    placeGeoId: city.placeGeoId || "",
    routePath: getMemberSeoCityPath(city, taskSlug),
    centroid: city.centroid || null,
    publicArea: city.publicArea || null,
  };
};

const buildMemberSeoCity = (
  city = {},
  taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG,
) => {
  const stateSlug = stateSlugFor(city);
  const citySlug = citySlugFor(city);
  const canonicalPath = getMemberSeoCityPath(
    Object.assign({}, city, { stateSlug, citySlug }),
    taskSlug,
  );

  return Object.assign({}, city, {
    stateSlug,
    citySlug,
    canonicalPath,
    memberSeoTaskSlug: taskSlug,
    landingLocationContext: buildMemberSeoLocationContext(
      Object.assign({}, city, { stateSlug, citySlug }),
      taskSlug,
    ),
  });
};

const memberSeoMetadata = Object.assign(
  {},
  memberSeoCityInventory.metadata || {},
  {
    canonicalRoutePattern: MEMBER_SEO_ROUTE_PATTERN,
    canonicalDecisions: MEMBER_SEO_CANONICAL_DECISIONS,
  },
);

const memberSeoCities = (memberSeoCityInventory.cities || [])
  .map((city) => buildMemberSeoCity(city))
  .filter((city) => city.stateSlug && city.citySlug)
  .sort(
    (a, b) =>
      String(a.stateName || "").localeCompare(String(b.stateName || "")) ||
      String(a.city || "").localeCompare(String(b.city || "")) ||
      String(a.id || "").localeCompare(String(b.id || "")),
  );

const memberSeoCitiesBySlug = new Map(
  memberSeoCities.map((city) => [city.slug, city]),
);

const memberSeoCityPathEntries = getMemberSeoTaskSlugs().flatMap((taskSlug) =>
  memberSeoCities.map((city) => {
    const canonicalPath = getMemberSeoCityPath(city, taskSlug);
    return [
      canonicalPath,
      Object.assign({}, city, {
        canonicalPath,
        memberSeoTaskSlug: taskSlug,
      }),
    ];
  }),
);

const memberSeoCitiesByPath = new Map(memberSeoCityPathEntries);

const memberSeoStateGroups = Array.from(
  memberSeoCities
    .reduce((groups, city) => {
      if (!groups.has(city.stateSlug)) {
        groups.set(city.stateSlug, {
          slug: city.stateSlug,
          stateSlug: city.stateSlug,
          stateCode: city.stateCode,
          stateName: city.stateName,
          taskSlug: city.memberSeoTaskSlug,
          cities: [],
        });
      }
      groups.get(city.stateSlug).cities.push(city);
      return groups;
    }, new Map())
    .values(),
)
  .map((group) =>
    Object.assign({}, group, {
      cities: group.cities.slice().sort((a, b) => a.city.localeCompare(b.city)),
    }),
  )
  .sort((a, b) => a.stateName.localeCompare(b.stateName));

const memberSeoStatesBySlug = new Map(
  memberSeoStateGroups.map((stateGroup) => [stateGroup.slug, stateGroup]),
);

const getMemberSeoSignupPath = (
  city = {},
  taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG,
) => {
  const task = getMemberSeoTask(taskSlug);
  const params = new URLSearchParams({
    source: "member_city_seo",
    sourceFamily: "member_seo",
    task: taskSlug,
    taskLabel: task?.label || "",
    taskGroup: task?.taskKey || taskSlug,
    city: city.city || "",
    state: city.stateCode || "",
    placeGeoId: city.placeGeoId || "",
    routePath: getMemberSeoCityPath(city, taskSlug),
  });
  return `/get-started?${params.toString()}`;
};

const renderMemberSeoTemplate = (template = "", city = {}) => {
  const stateCode = city.stateCode || "";
  const stateName = city.stateName || "";
  return String(template || "")
    .replace(/\{city\}/gu, city.city || "")
    .replace(/\{stateCode\}/gu, stateCode)
    .replace(/\{state\}/gu, stateName || stateCode);
};

const getMemberSeoRouteHero = (route = {}) => {
  const task = route.memberSeoTask || {};
  const location = route.memberSeoLocation || route.memberSeoCity || {};
  const localSeo = task.localSeo || {};

  return {
    headline:
      route.landingHero?.headline ||
      renderMemberSeoTemplate(localSeo.h1Template, location) ||
      task.h1 ||
      route.title ||
      "",
    description:
      route.landingHero?.description ||
      renderMemberSeoTemplate(localSeo.supportingCopyTemplate, location) ||
      route.description ||
      "",
  };
};

const buildMemberSeoCityRoute = (
  city,
  taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG,
) => {
  if (!city) return null;
  const task = getMemberSeoTask(taskSlug);
  if (!task) return null;
  const serviceRoute = getMemberSeoServiceRoute(taskSlug);
  const servicePage = publicSeo.servicePages?.[task.serviceKey] || {};
  const localSeo = task.localSeo || servicePage.localSeo || {};
  const locationContext = buildMemberSeoLocationContext(city, taskSlug);
  const canonicalPath = getMemberSeoCityPath(city, taskSlug);
  const indexable = city.eligibleForIndex !== false;
  const title =
    renderMemberSeoTemplate(localSeo.titleTemplate, city) ||
    `${task.label} in ${city.city}, ${city.stateName} | Linked Lives`;
  const description =
    renderMemberSeoTemplate(localSeo.descriptionTemplate, city) ||
    `Book ${task.label.toLowerCase()} in ${city.city}, ${city.stateCode} with local helper availability, pricing, task examples, and practical visit details from Linked Lives.`;

  return applyMemberSeoTitleExperiment({
    path: canonicalPath,
    canonicalUrl: absoluteUrl(canonicalPath),
    title,
    description,
    serviceKey: task.serviceKey,
    lastmod: latestIsoDate(
      city.lastContentUpdatedAt,
      task.lastmod,
      publicSeo.lastmod,
    ),
    indexable,
    primaryImage:
      servicePage.image?.url ||
      serviceRoute.primaryImage ||
      publicSeo.defaultImage,
    schemaTypes: ["Organization", "WebPage", "Service"],
    memberSeoRouteKind: "city",
    seoVersion: task.seoVersion,
    seoCohort: task.cohort,
    memberSeoTask: task,
    reviewTaskTags: task.reviewTaskTags || null,
    memberSeoLocation: locationContext,
    memberSeoCity: Object.assign({}, city, {
      canonicalPath,
      memberSeoTaskSlug: taskSlug,
    }),
    landingContext: {
      source: task.landingSource,
      task,
      taskSlug,
      serviceKey: task.serviceKey,
      manualLocationContext: locationContext,
    },
  });
};

const buildMemberSeoTaskRootRoute = (
  taskSlug = MEMBER_SEO_DEFAULT_TASK_SLUG,
) => {
  const task = getMemberSeoTask(taskSlug);
  if (!task) return null;
  const serviceRoute = getMemberSeoServiceRoute(taskSlug);
  const servicePage = publicSeo.servicePages?.[task.serviceKey] || {};
  const canonicalPath = getMemberSeoTaskRootPath(taskSlug);
  let serviceRoutePath = "";
  if (task.serviceRoutePath) {
    serviceRoutePath = normalizePath(task.serviceRoutePath);
  } else if (serviceRoute.path) {
    serviceRoutePath = normalizePath(serviceRoute.path);
  }
  const title = `${task.label} | Linked Lives`;
  const description =
    task.rootDescription ||
    serviceRoute.description ||
    servicePage.intro ||
    `Book reliable, friendly helpers for ${task.label.toLowerCase()} through Linked Lives.`;

  return {
    path: canonicalPath,
    canonicalUrl: absoluteUrl(canonicalPath),
    serviceRoutePath,
    title,
    description,
    serviceKey: task.serviceKey,
    lastmod: latestIsoDate(
      serviceRoute.lastmod,
      task.lastmod,
      memberSeoMetadata.lastmod,
      publicSeo.lastmod,
    ),
    indexable: true,
    primaryImage:
      servicePage.image?.url ||
      serviceRoute.primaryImage ||
      publicSeo.defaultImage,
    schemaTypes: ["Organization", "WebPage", "Service"],
    memberSeoRouteKind: "task-root",
    seoVersion: task.seoVersion,
    seoCohort: task.cohort,
    memberSeoTask: task,
    landingHero: {
      headline: task.h1,
      description,
    },
    landingContext: {
      source: task.landingSource,
      task,
      taskSlug,
      serviceKey: task.serviceKey,
      manualLocationContext: null,
    },
  };
};

const getMemberSeoTaskRootRoutes = () =>
  getMemberSeoTaskSlugs()
    .map((taskSlug) => buildMemberSeoTaskRootRoute(taskSlug))
    .filter(Boolean);

const getMemberSeoRoutes = () =>
  getMemberSeoTaskRootRoutes().concat(getMemberSeoCityRoutes());

const getMemberSeoCityRoutes = () => {
  return getMemberSeoTaskSlugs().flatMap((taskSlug) =>
    memberSeoCities
      .map((city) => buildMemberSeoCityRoute(city, taskSlug))
      .filter(Boolean),
  );
};

const getMemberSeoCityBySlugs = (stateSlug, citySlug) =>
  memberSeoCitiesBySlug.get(`${stateSlug}/${citySlug}`) || null;

const getMemberSeoStateBySlug = (stateSlug) =>
  memberSeoStatesBySlug.get(stateSlug) || null;

const getMemberSeoCityRouteBySlugs = (taskSlug, stateSlug, citySlug) => {
  const city = getMemberSeoCityBySlugs(stateSlug, citySlug);
  if (!city || !getMemberSeoTask(taskSlug)) return null;
  return buildMemberSeoCityRoute(city, taskSlug);
};

const getMemberSeoRouteByPath = (routePath) => {
  const normalized = normalizePath(routePath);
  const taskRootRoute = getMemberSeoTaskRootRoutes().find(
    (route) => route.path === normalized,
  );
  if (taskRootRoute) return taskRootRoute;
  const city = memberSeoCitiesByPath.get(normalized);
  if (!city) return null;
  return buildMemberSeoCityRoute(city, city.memberSeoTaskSlug);
};

const getMemberSeoDiscoveryLinks = (route = {}, nearbyLimit = 6) => {
  if (!route?.memberSeoRouteKind || !route?.memberSeoTask) {
    return {
      taskRootLink: null,
      taskLinks: Object.values(MEMBER_SEO_TASKS).map((task) => ({
        name: task.h1,
        path: task.canonicalBasePath,
      })),
      nearbyCityLinks: [],
      representativeCityLinks: [],
    };
  }
  const location = route.memberSeoLocation || null;
  const claimedPaths = new Set([route.path]);
  const claimLink = (link) => {
    if (!link?.path || claimedPaths.has(link.path)) return null;
    claimedPaths.add(link.path);
    return link;
  };
  const taskRootLink = claimLink({
    name: route.memberSeoTask.h1,
    path: route.memberSeoTask.canonicalBasePath,
  });
  const taskLinks = Object.values(MEMBER_SEO_TASKS)
    .map((task) =>
      claimLink({
        name: task.h1,
        path: location
          ? getMemberSeoCityPath(location, task.taskSlug)
          : task.canonicalBasePath,
      }),
    )
    .filter(Boolean);
  const currentState = location
    ? getMemberSeoStateBySlug(location.stateSlug)
    : null;
  const eligibleStateCities = (currentState?.cities || []).filter(
    (city) => city.eligibleForIndex !== false,
  );
  const currentCityIndex = eligibleStateCities.findIndex(
    (city) =>
      stateSlugFor(city) === location?.stateSlug &&
      citySlugFor(city) === location?.citySlug,
  );
  const boundedNearbyLimit = Math.min(
    Math.max(0, Number(nearbyLimit) || 0),
    Math.max(0, eligibleStateCities.length - 1),
  );
  const nearbyCityLinks = Array.from(
    { length: boundedNearbyLimit },
    (_, index) => {
      const distance = Math.floor(index / 2) + 1;
      const direction = index % 2 === 0 ? 1 : -1;
      const cityIndex =
        (currentCityIndex +
          direction * distance +
          eligibleStateCities.length) %
        eligibleStateCities.length;
      return eligibleStateCities[cityIndex];
    },
  )
    .map((city) =>
      claimLink({
        name: `${city.city}, ${city.stateCode}`,
        path: getMemberSeoCityPath(city, route.memberSeoTask.taskSlug),
      }),
    )
    .filter(Boolean);
  const representativeCityLinks =
    route.memberSeoRouteKind === "task-root"
      ? memberSeoStateGroups
          .map((stateGroup) =>
            (stateGroup.cities || []).find(
              (city) => city.eligibleForIndex !== false,
            ),
          )
          .filter(Boolean)
          .map((city) =>
            claimLink({
              name: `${city.city}, ${city.stateCode}`,
              path: getMemberSeoCityPath(city, route.memberSeoTask.taskSlug),
            }),
          )
          .filter(Boolean)
      : [];

  return {
    taskRootLink,
    taskLinks,
    nearbyCityLinks,
    representativeCityLinks,
  };
};

module.exports = {
  MEMBER_SEO_CANONICAL_DECISIONS,
  MEMBER_SEO_DEFAULT_TASK_SLUG,
  MEMBER_SEO_ROUTE_PATTERN,
  MEMBER_SEO_TASKS,
  assertUniqueMemberSeoTasks,
  absoluteUrl,
  buildMemberSeoCityRoute,
  buildMemberSeoLocationContext,
  buildMemberSeoTaskRootRoute,
  citySlugFor,
  getMemberSeoCityBySlugs,
  getMemberSeoCityPath,
  getMemberSeoCityRouteBySlugs,
  getMemberSeoCityRoutes,
  getMemberSeoDiscoveryLinks,
  getMemberSeoRouteByPath,
  getMemberSeoRouteHero,
  getMemberSeoRoutes,
  getMemberSeoServiceRoute,
  getMemberSeoSignupPath,
  getMemberSeoStateBySlug,
  getMemberSeoTask,
  getMemberSeoTaskByProductAlias,
  getMemberSeoTaskRootPath,
  getMemberSeoTaskRootRoutes,
  getMemberSeoTaskSlugs,
  memberSeoCities,
  memberSeoCitiesByPath,
  memberSeoCitiesBySlug,
  memberSeoMetadata,
  memberSeoTaskByProductAlias,
  memberSeoStateGroups,
  memberSeoStatesBySlug,
  normalizePath,
  renderMemberSeoTemplate,
  stateSlugFor,
};
